import { lazy, Suspense, type ComponentType } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { AppShell } from "@/components/layout/AppShell";
import { PortalShell } from "@/components/layout/PortalShell";
import { PortalGate } from "@/components/PortalGate";
import { PortalProvider } from "@/state/portal";
import { AccountLayout } from "@/components/layout/AccountLayout";
import { AccountRootRedirect } from "@/components/AccountRootRedirect";
import { AccountContextProvider } from "@/state/accountContext";
import { MODULES } from "@/config/modules";
import { usePerm } from "@/lib/perms";

function Fallback() {
  return (
    <div className="flex items-center justify-center gap-2 py-24 text-muted-foreground">
      <Loader2 className="size-5 animate-spin" />
      Loading…
    </div>
  );
}

// A failed chunk fetch almost always means a deploy replaced the hashed asset
// files while this tab still holds the old index.html. Reload once to pick up
// the new build instead of surfacing the router error page; the sessionStorage
// guard stops a reload loop if the chunk is genuinely broken.
function loadOrReload(load: () => Promise<{ default: ComponentType }>) {
  return () =>
    load().then(
      (m) => { sessionStorage.removeItem("chunk-reloaded"); return m; },
      (e) => {
        if (!sessionStorage.getItem("chunk-reloaded")) {
          sessionStorage.setItem("chunk-reloaded", "1");
          window.location.reload();
          return new Promise<never>(() => {}); // page is reloading — never settle
        }
        throw e;
      },
    );
}

function withSuspense(load: () => Promise<{ default: ComponentType }>) {
  const C = lazy(loadOrReload(load));
  return (
    <Suspense fallback={<Fallback />}>
      <C />
    </Suspense>
  );
}

const NotFound = lazy(loadOrReload(() => import("@/pages/NotFound")));

// Role gate for module routes: the nav already hides gated modules; this
// stops direct-URL access too. UI convenience — the API is the boundary.
// While the permission map loads, show the fallback rather than deciding.
function CapGate({ cap, children }: { cap?: string; children: React.ReactNode }) {
  const { can, ready } = usePerm();
  if (!cap) return <>{children}</>;
  if (!ready) return <Fallback />;
  if (!can(cap)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

// Portal tree is wrapped in its own provider + gate so it's completely
// independent of the admin auth context.
function PortalRoot() {
  return (
    <PortalProvider>
      <PortalGate>
        <PortalShell />
      </PortalGate>
    </PortalProvider>
  );
}

export const router = createBrowserRouter([
  // Standalone pop-outs (no shell) — sharable as their own window.
  // Wrapped in the account context so the forecast tab knows which account is active.
  { path: "/present/missed-call-math", element: <AccountContextProvider>{withSuspense(() => import("@/pages/MissedCallPresent"))}</AccountContextProvider> },

  // Portal verify — no shell, no gate (this is how you sign in).
  {
    path: "/portal/verify",
    element: (
      <PortalProvider>{withSuspense(() => import("@/pages/portal/PortalVerify"))}</PortalProvider>
    ),
  },
  // Portal signed-in shell.
  {
    path: "/portal",
    element: <PortalRoot />,
    children: [
      { index: true, element: withSuspense(() => import("@/pages/portal/PortalHome")) },
      { path: "form/:slug", element: withSuspense(() => import("@/pages/portal/PortalForm")) },
      { path: "tickets", element: withSuspense(() => import("@/pages/portal/PortalTickets")) },
      { path: "tickets/:id", element: withSuspense(() => import("@/pages/portal/PortalTicket")) },
      { path: "qr", element: withSuspense(() => import("@/pages/portal/PortalQr")) },
      { path: "help", element: withSuspense(() => import("@/pages/portal/PortalHelp")) },
      { path: "help/:id", element: withSuspense(() => import("@/pages/portal/PortalHelp")) },
    ],
  },

  // Admin app.
  {
    path: "/",
    element: (
      <AccountContextProvider>
        <AppShell />
      </AccountContextProvider>
    ),
    children: [
      // Card-based Home (Field Suite workspace pattern) — one card per module.
      { index: true, element: withSuspense(() => import("@/pages/Home")) },
      { path: "launcher", element: <Navigate to="/" replace /> },
      ...MODULES.map((m) => ({
        path: m.id,
        element: <CapGate cap={m.perm}>{withSuspense(m.load)}</CapGate>,
      })),
      { path: "audits/:id", element: <CapGate cap="audits.data.READ">{withSuspense(() => import("@/pages/Audits"))}</CapGate> },
      { path: "wiki/:slug", element: withSuspense(() => import("@/pages/WikiPage")) },
      { path: "help", element: withSuspense(() => import("@/pages/Help")) },
      { path: "help/:articleId", element: withSuspense(() => import("@/pages/Help")) },

      // Account-scoped routes. URL is the source of truth: /accounts/:accountId/*
      // Bare /accounts bounces to the current account (or Pipeline if none).
      { path: "accounts", element: <AccountRootRedirect /> },
      {
        path: "accounts/:accountId",
        element: <AccountLayout />,
        children: [
          { index: true, element: <Navigate to="overview" replace /> },
          { path: "overview", element: withSuspense(() => import("@/pages/account/AccountOverview")) },
          // Account-scoped modules — reuse existing pages, which read
          // useParams().accountId to filter to the current account.
          { path: "live-call", element: withSuspense(() => import("@/pages/LiveCall")) },
          { path: "fulfillment", element: <CapGate cap="checklists.customer.READ">{withSuspense(() => import("@/pages/Fulfillment"))}</CapGate> },
          // booking-board is agency-wide now (Insights); the old account-scoped
          // URL bounces to the board rather than 404ing stale links.
          { path: "booking-board", element: <Navigate to="/booking-board" replace /> },
          { path: "tickets", element: <CapGate cap="tickets.data.READ">{withSuspense(() => import("@/pages/Tickets"))}</CapGate> },
          { path: "client-portal", element: <CapGate cap="portal.forms.READ">{withSuspense(() => import("@/pages/ClientPortal"))}</CapGate> },
        ],
      },
      {
        path: "*",
        element: (
          <Suspense fallback={<Fallback />}>
            <NotFound />
          </Suspense>
        ),
      },
    ],
  },
]);
